import { compact, map } from 'lodash';
import type { IAccountIntegrations } from './AccountIntegrations.interface';
import AccountIntegrationsLoanQToken from './AccountIntegrationsLoanQToken';

export default class AccountIntegrations implements IAccountIntegrations {
  ali: {
    enabled: boolean,
    brokerId: string,
    brokerEmail: string
  };

  allianz: {
    enabled: boolean,
    placingOfficerCode: string
  };

  bankStatements: {
    enabled: boolean,
    code: string,
    isMultiBankEnabled: boolean,
    emailCustomContent: string
  };

  cashDeck: {
    enabled: boolean,
    apiKey: string
  };

  compareClub: {
    enabled: boolean,
    brokerCode: string
  };

  creditFixSolution: {
    enabled: boolean
  };

  dropbox: {
    enabled: boolean,
    token: any
  };

  equifax: {
    enabled: boolean,
    username: string,
    password: string
  };

  flex: {
    enabled: boolean,
    email: string,
    username: string,
    password: string
  };

  googleContacts: {
    enabled: boolean,
    token: any
  };

  googleDrive: {
    enabled: boolean,
    token: any
  };

  googleTasks: {
    enabled: boolean,
    token: any
  };

  honey: {
    enabled: boolean,
    brokerId: string
  };

  illion: {
    enabled: boolean,
    userKey: any,
    password: any,
    subscriberId: any
  };

  lid: {
    enabled: boolean,
    trackingCode: string
  };

  loanq: {
    enabled: boolean,
    tokens: Array<Object>
  };

  mercury: {
    enabled: boolean,
    apiKey: string,
    token: string
  };

  mogo: {
    enabled: boolean
  };

  movingHub: {
    enabled: boolean,
    agentId: string
  };

  myConnect: {
    enabled: boolean,
    agentId: string,
    managerId: string
  };

  oneDrive: {
    enabled: boolean,
    token: any
  };

  outlookContacts: {
    enabled: boolean,
    token: any
  };

  outlookMail: {
    enabled: boolean,
    token: any
  };

  outlookTasks: {
    enabled: boolean,
    token: any
  };

  pepperResolve: {
    enabled: boolean,
    brokerId: string
  };

  priceFinder: {
    enabled: boolean,
    token: any
  };

  rpData: {
    enabled: boolean,
    toke: any
  };

  settleEasy: {
    enabled: boolean
  };

  slack: {
    enabled: boolean,
    token: string
  };

  vero: {
    enabled: boolean,
    brokerIdNumber: string
  };

  static build = (data: IAccountIntegrations) => new AccountIntegrations(data);

  static blank = () => new AccountIntegrations();

  /**
   * Constructor
   * @param {IAccountIntegrations} data
   */
  constructor(data?: IAccountIntegrations) {
    this.ali = {
      enabled: data?.ali?.enabled === true,
      brokerId: data?.ali?.brokerId || '',
      brokerEmail: data?.ali?.brokerEmail || ''
    };
    this.allianz = {
      enabled: data?.allianz?.enabled === true,
      placingOfficerCode: data?.allianz?.placingOfficerCode || ''
    };
    this.bankStatements = {
      enabled: data?.bankStatements?.enabled === true,
      code: data?.bankStatements?.code || '',
      isMultiBankEnabled: data?.bankStatements?.isMultiBankEnabled === true,
      emailCustomContent: data?.bankStatements?.emailCustomContent || ''
    };
    this.cashDeck = {
      enabled: data?.cashDeck?.enabled === true,
      apiKey: data?.cashDeck?.apiKey || ''
    };
    this.compareClub = {
      enabled: data?.compareClub?.enabled === true,
      brokerCode: data?.compareClub?.brokerCode || ''
    };
    this.creditFixSolution = {
      enabled: data?.creditFixSolution?.enabled === true
    };
    this.dropbox = {
      enabled: data?.dropbox?.enabled === true,
      token: data?.dropbox?.token || null
    };
    this.equifax = {
      enabled: data?.equifax?.enabled === true,
      username: data?.equifax?.username || '',
      password: data?.equifax?.password || ''
    };
    this.flex = {
      enabled: data?.flex?.enabled === true,
      email: data?.flex?.email || '',
      username: data?.flex?.username || '',
      password: data?.flex?.password || ''
    };
    this.googleContacts = {
      enabled: data?.googleContacts?.enabled === true,
      token: data?.googleContacts?.token || null
    };
    this.googleDrive = {
      enabled: data?.googleDrive?.enabled === true,
      token: data?.googleDrive?.token || null
    };
    this.googleTasks = {
      enabled: data?.googleTasks?.enabled === true,
      token: data?.googleTasks?.token || null
    };
    this.honey = {
      enabled: data?.honey?.enabled === true,
      brokerId: data?.honey?.brokerId || ''
    };
    this.illion = {
      enabled: data?.illion?.enabled === true,
      userKey: data?.illion?.userKey || null,
      password: data?.illion?.password || null,
      subscriberId: data?.illion?.subscriberId || null
    };
    this.lid = {
      enabled: data?.lid?.enabled === true,
      trackingCode: data?.lid?.trackingCode || ''
    };

    this.loanq = {
      enabled: data?.loanq?.enabled === true,
      tokens: compact(
        map(data?.loanq?.tokens, (obj) => {
          const out = new AccountIntegrationsLoanQToken(obj);
          return out && out.id ? out : null;
        })
      )
    };
    this.loanq.enabled = !!(this.loanq.tokens && this.loanq.tokens.length);

    this.mercury = {
      enabled: data?.mercury?.enabled === true,
      apiKey: data?.mercury?.apiKey || '',
      token: data?.mercury?.token || ''
    };
    this.mogo = {
      enabled: data?.mogo?.enabled === true
    };
    this.movingHub = {
      enabled: data?.movingHub?.enabled === true,
      agentId: data?.movingHub?.agentId || ''
    };
    this.myConnect = {
      enabled: data?.myConnect?.enabled === true,
      agentId: data?.myConnect?.agentId || '',
      managerId: data?.myConnect?.managerId || ''
    };
    this.oneDrive = {
      enabled: data?.oneDrive?.enabled === true,
      token: data?.oneDrive?.token || null
    };
    this.outlookContacts = {
      enabled: data?.outlookContacts?.enabled === true,
      token: data?.outlookContacts?.token || null
    };
    this.outlookMail = {
      enabled: data?.outlookMail?.enabled === true,
      token: data?.outlookMail?.token || null
    };
    this.outlookTasks = {
      enabled: data?.outlookTasks?.enabled === true,
      token: data?.outlookTasks?.token || null
    };
    this.pepperResolve = {
      enabled: data?.pepperResolve?.enabled === true,
      brokerId: data?.pepperResolve?.brokerId || ''
    };
    this.priceFinder = {
      enabled: data?.priceFinder?.enabled === true,
      token: data?.priceFinder?.token || null
    };
    this.rpData = {
      enabled: data?.rpData?.enabled === true,
      token: data?.rpData?.token || null
    };
    this.settleEasy = {
      enabled: data?.settleEasy?.enabled === true
    };
    this.slack = {
      enabled: data?.slack?.enabled === true,
      token: data?.slack?.token || ''
    };
    this.vero = {
      enabled: data?.vero?.enabled === true,
      brokerIdNumber: data?.vero?.brokerIdNumber || ''
    };
  }
}
